// Gen 9 stat formula at level 50 (VGC fixed level).
//
// HP formula differs from other stats:
//   HP = floor(((2 * base + iv + floor(ev / 4)) * 50) / 100) + 50 + 10
//   stat = floor((floor(((2 * base + iv + floor(ev / 4)) * 50) / 100) + 5) * natureMult)
//
// Nature multipliers: 1.1 for the boosted stat, 0.9 for the hindered stat,
// 1.0 otherwise. Neutral natures (Hardy, Docile, Serious, Bashful, Quirky)
// have no boosted/hindered stat.
//
// This duplicates math that @smogon/calc runs server-side, but the Gen 9
// formula is generation-stable — calc/server/client drift risk is zero
// for this specific math. Server round-trips on every EV slider tick
// would feel sluggish; this lets the editor display live final stats
// without an API call per drag event.

export type Stat = 'hp' | 'atk' | 'def' | 'spa' | 'spd' | 'spe';

export type StatBlock = Record<Stat, number>;

// Maps each nature to its {boosted, hindered} stat. Null entries are neutral.
// Source: pokemon mechanics, stable across generations.
const NATURE_EFFECTS: Record<string, { up: Stat; down: Stat } | null> = {
  Hardy: null,
  Lonely: { up: 'atk', down: 'def' },
  Brave: { up: 'atk', down: 'spe' },
  Adamant: { up: 'atk', down: 'spa' },
  Naughty: { up: 'atk', down: 'spd' },
  Bold: { up: 'def', down: 'atk' },
  Docile: null,
  Relaxed: { up: 'def', down: 'spe' },
  Impish: { up: 'def', down: 'spa' },
  Lax: { up: 'def', down: 'spd' },
  Timid: { up: 'spe', down: 'atk' },
  Hasty: { up: 'spe', down: 'def' },
  Serious: null,
  Jolly: { up: 'spe', down: 'spa' },
  Naive: { up: 'spe', down: 'spd' },
  Modest: { up: 'spa', down: 'atk' },
  Mild: { up: 'spa', down: 'def' },
  Quiet: { up: 'spa', down: 'spe' },
  Bashful: null,
  Rash: { up: 'spa', down: 'spd' },
  Calm: { up: 'spd', down: 'atk' },
  Gentle: { up: 'spd', down: 'def' },
  Sassy: { up: 'spd', down: 'spe' },
  Careful: { up: 'spd', down: 'spa' },
  Quirky: null,
};

export const NATURE_LIST: readonly string[] = Object.freeze(Object.keys(NATURE_EFFECTS));

export function natureMultiplier(nature: string | undefined, stat: Stat): number {
  if (!nature) return 1.0;
  const effect = NATURE_EFFECTS[nature];
  if (!effect) return 1.0;
  if (effect.up === stat) return 1.1;
  if (effect.down === stat) return 0.9;
  return 1.0;
}

// Returns ▲ / ▼ / null for a nature's effect on the given stat — for the
// EvRow's nature glyph indicator.
export function natureGlyph(nature: string | undefined, stat: Stat): '▲' | '▼' | null {
  const m = natureMultiplier(nature, stat);
  if (m > 1) return '▲';
  if (m < 1) return '▼';
  return null;
}

const LEVEL = 50;

// Two flavours of stat math:
//   - SV-mainline: EVs 0–252 per stat, 510 total; uses the level-50 formula
//   - Champions Reg M-A: SP 0–32 per stat, 66 total; uses PS's `statModify`
//     (`data/mods/champions/scripts.ts:9-22`): HP = base + sp + 75, others
//     = (base + sp + 20) × nature.
// The system is selected at the call site (Editor passes its regulation's
// system; agent/server paths use SV after rescaling SP → EV upstream).
export type StatSystem = 'sp' | 'ev';

// Final stat at level 50. Shedinja's HP=1 case is handled (always 1).
//
// The `points` argument is interpreted per `system`:
//   - 'ev': mainline EVs (0–252). Floor(ev/4) contributes to the formula.
//   - 'sp': Champions Stat Points (0–32). Adds directly to the PS formula.
export function finalStat(
  stat: Stat,
  base: number,
  points: number,
  iv: number,
  nature: string | undefined,
  system: StatSystem = 'ev',
): number {
  if (system === 'sp') {
    // PS Champions formula. Note: doesn't use IV — Champions hard-locks IVs
    // to 31, so the IV term is folded into the constant (75 for HP, 20 for
    // others). Verified algebraically: SV at 31 IV ≡ Champions formula at
    // SP×8 EV equivalent for all integer SP values.
    if (stat === 'hp') {
      if (base === 1) return 1; // Shedinja
      return base + points + 75;
    }
    const raw = base + points + 20;
    return Math.floor(raw * natureMultiplier(nature, stat));
  }
  // SV-mainline EVs
  const evContrib = Math.floor(points / 4);
  if (stat === 'hp') {
    if (base === 1) return 1;
    return Math.floor(((2 * base + iv + evContrib) * LEVEL) / 100) + LEVEL + 10;
  }
  const raw = Math.floor(((2 * base + iv + evContrib) * LEVEL) / 100) + 5;
  return Math.floor(raw * natureMultiplier(nature, stat));
}

// Compute all six final stats. EVs/IVs default to 0/31 when omitted (the
// blank-spread state used when starting a fresh slot).
export function finalStats(input: {
  base: StatBlock;
  evs?: Partial<StatBlock>;
  ivs?: Partial<StatBlock>;
  nature?: string;
  system?: StatSystem;
}): StatBlock {
  const stats: Stat[] = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
  const out = {} as StatBlock;
  for (const s of stats) {
    out[s] = finalStat(
      s,
      input.base[s],
      input.evs?.[s] ?? 0,
      input.ivs?.[s] ?? 31,
      input.nature,
      input.system,
    );
  }
  return out;
}

// Sum of all EV/SP across the six stats. Used to enforce the total cap and
// display the running total in the editor.
export function totalEvs(evs: Partial<StatBlock> | undefined): number {
  if (!evs) return 0;
  return (
    (evs.hp ?? 0) +
    (evs.atk ?? 0) +
    (evs.def ?? 0) +
    (evs.spa ?? 0) +
    (evs.spd ?? 0) +
    (evs.spe ?? 0)
  );
}

// Clamp and snap an EV/SP value to the system's grid.
//   - SV: cap 252 per stat, step 4 (EVs only matter at multiples of 4)
//   - Champions SP: cap 32 per stat, step 1 (every SP is one final stat point)
export function snapEv(value: number, system: StatSystem = 'ev'): number {
  if (Number.isNaN(value)) return 0;
  if (system === 'sp') {
    return Math.max(0, Math.min(32, Math.round(value)));
  }
  const clamped = Math.max(0, Math.min(252, value));
  return Math.round(clamped / 4) * 4;
}

// Headroom remaining for `stat` without exceeding the system's total cap.
// Pass perStatCap + totalCap from the regulation's evBudget (or leave the
// defaults for SV-mainline).
export function evHeadroom(
  stat: Stat,
  evs: Partial<StatBlock> | undefined,
  totalCap = 510,
  perStatCap = 252,
): number {
  const total = totalEvs(evs);
  const current = evs?.[stat] ?? 0;
  const otherTotal = total - current;
  return Math.max(0, Math.min(perStatCap, totalCap - otherTotal));
}
